/**
 * Safe numeric evaluator for Max `@@ … @@` blocks: arithmetic, elementary functions,
 * \\sum_{var=lo..hi}{body}, \\prod_{…}{…}, \\integral_{…}{…} (also `sum_{…}`, `prod_{…}`, `integral_{…}`),
 * repeat_{count}{payload} (string result),
 * `name=table{rows,cols}` string grids and `name[row][col]=payload` cell writes,
 * `for{i=a..b}{…}` loops (body runs as statements; index in `env`; block value is each iteration’s result concatenated),
 * pipe concatenation `a | b | c` (string result; numbers use `formatMaxCalcNum`; stats-style `4 | x`
 * with spaces around `|` rewrites to a labeled `4 | value(x)`; two-digit+ labels allow `64|x`),
 * and `show{L}{R}` formats `L | R` for numeric sides,
 * in double-quoted strings, env names interpolate inside `$…$` (e.g. `"$x^i$"` with `i` in env → `$x^{n}$` for TeX),
 * semicolon-separated statements, and `name = expr` assignments (see `evaluateMaxNumericExpression`).
 */

export type MaxCalcResult = { ok: true; value: string } | { ok: false; error: string };

/** String tables keyed by name; mutated across `@@…@@` blocks like numeric `vars`. */
export type MaxCalcTables = Record<string, string[][]>;

export type MaxCalcUserFuncs = Record<string, { params: string[]; bodySrc: string }>;

type Tok =
  | { k: "num"; v: number }
  | { k: "str"; v: string }
  | { k: "id"; name: string }
  | {
      k:
        | "+"
        | "-"
        | "*"
        | "/"
        | "^"
        | "("
        | ")"
        | "{"
        | "}"
        | "["
        | "]"
        | "_"
        | ","
        | "="
        | ";"
        | ".."
        | "|"
        | "eof";
    };

const UNARY_FUN: Record<string, (x: number) => number> = {
  sin: Math.sin,
  cos: Math.cos,
  tan: Math.tan,
  asin: Math.asin,
  acos: Math.acos,
  atan: Math.atan,
  sinh: Math.sinh,
  cosh: Math.cosh,
  tanh: Math.tanh,
  sqrt: Math.sqrt,
  abs: Math.abs,
  floor: Math.floor,
  ceil: Math.ceil,
  round: Math.round,
  exp: Math.exp,
  log: Math.log,
  ln: Math.log,
  log10: Math.log10,
  sign: (x: number) => Math.sign(x),
};

const BINARY_FUN: Record<string, (a: number, b: number) => number> = {
  atan2: Math.atan2,
  min: Math.min,
  max: Math.max,
  pow: Math.pow,
};

const RESERVED_ASSIGN = new Set<string>([
  "sum",
  "prod",
  "integral",
  "repeat",
  "show",
  "table",
  "for",
  "if",
  "pi",
  "e",
  ...Object.keys(UNARY_FUN),
  ...Object.keys(BINARY_FUN),
]);

const MAX_TABLE_SIDE = 256;
const MAX_TABLE_CELLS = 50_000;

/** Split on `;` at paren / brace / bracket depth 0 (respects double-quoted strings). */
function splitBySemicolon(src: string): string[] {
  const out: string[] = [];
  let start = 0;
  let paren = 0;
  let brace = 0;
  let bracket = 0;
  let inStr = false;
  let strEsc = false;
  for (let i = 0; i < src.length; i++) {
    const c = src[i]!;
    if (inStr) {
      if (strEsc) {
        strEsc = false;
      } else if (c === "\\") {
        strEsc = true;
      } else if (c === '"') {
        inStr = false;
      }
      continue;
    }
    if (c === '"') {
      inStr = true;
      continue;
    }
    if (c === "(") paren++;
    else if (c === ")") paren--;
    else if (c === "{") brace++;
    else if (c === "}") brace--;
    else if (c === "[") bracket++;
    else if (c === "]") bracket--;
    if (c === ";" && paren === 0 && brace === 0 && bracket === 0) {
      out.push(src.slice(start, i));
      start = i + 1;
    }
  }
  out.push(src.slice(start));
  return out;
}

/**
 * Stats-style `n | name` (integer panel count, bare variable) should read as `n | value`, not pipe
 * concatenation (`64` glued to `0.07…` → `640.07…`). Rewrite those statements to `show{n}{name}`.
 *
 * - Requires whitespace on both sides of `|` when the left literal is a single digit (`4 | x`),
 *   so `1|x` stays numeric concatenation.
 * - When the left literal has two or more digits, allow `64|x` or `64 | x` (still bare id on RHS).
 */
function maybeRewriteDisplayPipeStmt(trimmed: string): string {
  const spaced = /^(\d+)\s+\|\s+([a-zA-Z_][a-zA-Z0-9_]*)\s*$/.exec(trimmed);
  if (spaced) return `show{${spaced[1]!}}{${spaced[2]!}}`;
  const tight = /^(\d{2,})\s*\|\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*$/.exec(trimmed);
  if (tight) return `show{${tight[1]!}}{${tight[2]!}}`;
  return trimmed;
}

function lexMaxExpr(src: string): Tok[] | { err: string } {
  const out: Tok[] = [];
  let i = 0;
  const n = src.length;
  while (i < n) {
    const c = src[i]!;
    if (/\s/.test(c)) {
      i++;
      continue;
    }
    if (c === "." && src[i + 1] === ".") {
      out.push({ k: ".." });
      i += 2;
      continue;
    }
    if (c === '"') {
      let j = i + 1;
      let buf = "";
      let closed = false;
      while (j < n) {
        const ch = src[j]!;
        if (ch === '"') {
          j++;
          closed = true;
          break;
        }
        if (ch === "\\" && j + 1 < n) {
          const e = src[j + 1]!;
          if (e === "n") buf += "\n";
          else if (e === "t") buf += "\t";
          else if (e === '"' || e === "\\") buf += e;
          else buf += e;
          j += 2;
          continue;
        }
        buf += ch;
        j++;
      }
      if (!closed) return { err: "unclosed string" };
      out.push({ k: "str", v: buf });
      i = j;
      continue;
    }
    if (/[0-9]/.test(c) || (c === "." && /[0-9]/.test(src[i + 1] ?? ""))) {
      let j = i + 1;
      while (j < n && /[0-9]/.test(src[j]!)) j++;
      if (j < n && src[j] === "." && src[j + 1] !== ".") {
        j++;
        while (j < n && /[0-9]/.test(src[j]!)) j++;
      }
      if (j < n && /[eE]/.test(src[j]!)) {
        j++;
        if (j < n && /[+\-]/.test(src[j]!)) j++;
        while (j < n && /[0-9]/.test(src[j]!)) j++;
      }
      const raw = src.slice(i, j);
      const v = Number.parseFloat(raw);
      if (!Number.isFinite(v)) return { err: `bad number ${raw}` };
      out.push({ k: "num", v });
      i = j;
      continue;
    }
    if (c === "\\") {
      const slashKw = ["integral", "sum", "prod"] as const;
      let hit: (typeof slashKw)[number] | null = null;
      for (const kw of slashKw) {
        const start = i + 1;
        if (start + kw.length > n || src.slice(start, start + kw.length) !== kw) continue;
        const after = start + kw.length;
        if (after < n) {
          const ch = src[after]!;
          if (/[a-zA-Z0-9]/.test(ch) && ch !== "_") continue;
        }
        hit = kw;
        break;
      }
      if (hit) {
        out.push({ k: "id", name: `\\${hit}` });
        i += 1 + hit.length;
        continue;
      }
      return { err: `unexpected character “${c}”` };
    }
    if (/[a-zA-Z]/.test(c)) {
      let j = i + 1;
      while (j < n && /[a-zA-Z0-9]/.test(src[j]!)) j++;
      // Keep `sum_{…}`, `prod_{…}`, `integral_{…}`, `repeat_{…}` as `sum` + `_` + `{…}`, not `sum_`.
      while (j < n && src[j] === "_") {
        if (j + 1 < n && src[j + 1] === "{") break;
        j++;
        while (j < n && /[a-zA-Z0-9_]/.test(src[j]!)) j++;
      }
      out.push({ k: "id", name: src.slice(i, j) });
      i = j;
      continue;
    }
    if ("+-*/^(){}[]_,=;|".includes(c)) {
      out.push({ k: c } as Tok);
      i++;
      continue;
    }
    return { err: `unexpected character “${c}”` };
  }
  out.push({ k: "eof" });
  return out;
}

/**
 * Slice [from, until) where `until` is the first stop token at paren depth 0 and inner-brace depth 0.
 * Inner `{` / `}` are balanced; a `}` with inner depth 0 closes the outer clause (range or body).
 */
function sliceUntil(
  toks: Tok[],
  from: number,
  stop: ".." | "}",
): { slice: Tok[]; until: number } | { err: string } {
  let paren = 0;
  let brace = 0;
  for (let j = from; j < toks.length; j++) {
    const t = toks[j]!;
    if (t.k === "eof") return { err: "unterminated expression" };
    if (t.k === "(") paren++;
    else if (t.k === ")") {
      paren--;
      if (paren < 0) return { err: "extra )" };
    } else if (t.k === "{") {
      brace++;
    } else if (t.k === "}") {
      if (brace > 0) {
        brace--;
      } else if (stop === "}" && paren === 0) {
        return { slice: toks.slice(from, j), until: j };
      } else {
        return { err: "extra }" };
      }
    } else if (t.k === ".." && stop === ".." && paren === 0 && brace === 0) {
      return { slice: toks.slice(from, j), until: j };
    }
  }
  return { err: "unterminated expression" };
}

/** Tokens inside `[` … `]`; `start` = first token after `[`. */
function sliceBracketExpr(
  toks: Tok[],
  start: number,
): { slice: Tok[]; end: number } | { err: string } {
  let depth = 0;
  for (let j = start; j < toks.length; j++) {
    const t = toks[j]!;
    if (t.k === "eof") return { err: "unclosed [" };
    if (t.k === "[") depth++;
    else if (t.k === "]") {
      if (depth === 0) return { slice: toks.slice(start, j), end: j + 1 };
      depth--;
    }
  }
  return { err: "unclosed [" };
}

/** Slice from `from` until `,` at paren/brace/bracket depth 0 (for `table{…,…}`). */
function sliceUntilComma(
  toks: Tok[],
  from: number,
): { slice: Tok[]; next: number } | { err: string } {
  let paren = 0;
  let brace = 0;
  let square = 0;
  for (let j = from; j < toks.length; j++) {
    const t = toks[j]!;
    if (t.k === "eof") return { err: "unterminated expression" };
    if (t.k === "(") paren++;
    else if (t.k === ")") {
      paren--;
      if (paren < 0) return { err: "extra )" };
    } else if (t.k === "{") brace++;
    else if (t.k === "}") {
      if (brace > 0) brace--;
      else return { err: "unexpected }" };
    } else if (t.k === "[") square++;
    else if (t.k === "]") {
      square--;
      if (square < 0) return { err: "extra ]" };
    } else if (t.k === "," && paren === 0 && brace === 0 && square === 0) {
      return { slice: toks.slice(from, j), next: j + 1 };
    }
  }
  return { err: "unterminated expression" };
}

export function formatMaxCalcNum(v: number): string {
  if (!Number.isFinite(v)) return Number.isNaN(v) ? "NaN" : v > 0 ? "∞" : "-∞";
  if (Object.is(v, -0)) return "0";
  const r = Math.round(v);
  if (Math.abs(v - r) < 1e-12 && Math.abs(r) < 1e15) return String(r);
  return String(Number(v.toPrecision(14)));
}

function escapeMaxRegexIdent(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** `^name` → `^{value}` then bare `name` words; skips TeX `\name`; only used inside `$…$` slices. */
function interpolateEnvInMathSlice(seg: string, env: Record<string, number>): string {
  const keys = Object.keys(env)
    .filter((k) => /^[a-zA-Z][a-zA-Z0-9]*$/.test(k))
    .sort((a, b) => b.length - a.length);
  let out = seg;
  for (const name of keys) {
    const val = formatMaxCalcNum(env[name]!);
    const esc = escapeMaxRegexIdent(name);
    out = out.replace(new RegExp(`(?<!\\\\)\\^${esc}\\b`, "g"), `^{${val}}`);
  }
  for (const name of keys) {
    const val = formatMaxCalcNum(env[name]!);
    const esc = escapeMaxRegexIdent(name);
    out = out.replace(new RegExp(`(?<!\\\\)\\b${esc}\\b`, "g"), val);
  }
  return out;
}

/**
 * In string literals, interpolate env into TeX-ish `$…$` regions only (so `"a"` stays literal when
 * `a` is a variable). Odd `$`-split segments (between delimiters) get `^i` → `^{n}` and word ids.
 */
export function interpolateEnvInMaxString(s: string, env: Record<string, number>): string {
  if (!s.includes("$")) return s;
  const parts = s.split("$");
  for (let p = 1; p < parts.length; p += 2) {
    parts[p] = interpolateEnvInMathSlice(parts[p]!, env);
  }
  return parts.join("$");
}

function integrateSimpson(f: (x: number) => number, a: number, b: number): number {
  if (!Number.isFinite(a) || !Number.isFinite(b)) return Number.NaN;
  if (a === b) return 0;
  let sign = 1;
  let lo = a;
  let hi = b;
  if (lo > hi) {
    sign = -1;
    [lo, hi] = [hi, lo];
  }
  let n = 4096;
  if (n % 2 !== 0) n++;
  const h = (hi - lo) / n;
  let sum = f(lo) + f(hi);
  for (let i = 1; i < n; i++) {
    const x = lo + i * h;
    sum += (i % 2 === 0 ? 2 : 4) * f(x);
  }
  return sign * (h / 3) * sum;
}

const MAX_REPEAT_COUNT = 1_000_000;

/** Resolve `{…}` body of `repeat_{n}{…}` to a string (quoted literal, number, variable, or numeric expression). */
function resolveRepeatPayload(slice: Tok[], env: Record<string, number>, funcs?: MaxCalcUserFuncs): string {
  const nonEof = slice.filter((t) => t.k !== "eof");
  if (nonEof.length === 0) throw new Error("repeat: empty body");
  if (nonEof.length === 1) {
    const t = nonEof[0]!;
    if (t.k === "str") return interpolateEnvInMaxString(t.v, env);
    if (t.k === "num") return String(t.v);
    if (t.k === "id") {
      if (!Object.prototype.hasOwnProperty.call(env, t.name)) {
        throw new Error(`unknown name “${t.name}” in repeat body`);
      }
      return String(env[t.name]!);
    }
  }
  const toksPlus = [...slice, { k: "eof" } as const];
  const pv = new Parser(toksPlus, env, funcs).parseValue();
  return typeof pv === "number" ? formatMaxCalcNum(pv) : pv;
}

class Parser {
  private i = 0;

  constructor(
    private readonly toks: Tok[],
    private readonly env: Record<string, number>,
    private readonly funcs?: MaxCalcUserFuncs,
  ) {}

  private peek(): Tok {
    return this.toks[this.i] ?? { k: "eof" };
  }

  private eat(expected?: Tok["k"]): Tok {
    const t = this.peek();
    if (expected && t.k !== expected) {
      throw new Error(`expected ${expected}`);
    }
    this.i++;
    return t;
  }

  parse(): number {
    const v = this.parseAddSub();
    if (this.peek().k !== "eof") {
      throw new Error("unexpected token after expression");
    }
    return v;
  }

  parseExpr(): number {
    return this.parseAddSub();
  }

  private parseAddSub(): number {
    let v = this.parseMulDiv();
    for (;;) {
      const t = this.peek();
      if (t.k === "+" || t.k === "-") {
        this.eat();
        const r = this.parseMulDiv();
        v = t.k === "+" ? v + r : v - r;
      } else break;
    }
    return v;
  }

  private parseMulDiv(): number {
    let v = this.parsePow();
    for (;;) {
      const t = this.peek();
      if (t.k === "*" || t.k === "/") {
        this.eat();
        const r = this.parsePow();
        v = t.k === "*" ? v * r : v / r;
      } else break;
    }
    return v;
  }

  private parsePow(): number {
    let v = this.parseUnary();
    if (this.peek().k === "^") {
      this.eat();
      const r = this.parsePow();
      v = Math.pow(v, r);
    }
    return v;
  }

  private parseUnary(): number {
    const t = this.peek();
    if (t.k === "+") {
      this.eat();
      return this.parseUnary();
    }
    if (t.k === "-") {
      this.eat();
      return -this.parseUnary();
    }
    return this.parsePrimaryNumber();
  }

  private parsePrimaryNumber(): number {
    const t = this.peek();
    if (t.k === "num") {
      this.eat();
      return t.v;
    }
    if (t.k === "(") {
      this.eat("(");
      const v = this.parseConcatValue();
      this.eat(")");
      if (typeof v !== "number") {
        throw new Error("expected number in ( )");
      }
      return v;
    }
    if (t.k === "id") {
      const name = t.name;
      if (
        name === "sum" ||
        name === "prod" ||
        name === "integral" ||
        name === "\\sum" ||
        name === "\\prod" ||
        name === "\\integral"
      ) {
        this.eat();
        const macro = (name.startsWith("\\") ? name.slice(1) : name) as "sum" | "prod" | "integral";
        return this.parseAggregateRest(macro);
      }
      this.eat();
      if (this.peek().k === "(") {
        return this.parseFuncall(name);
      }
      if (name === "pi") return Math.PI;
      if (name === "e") return Math.E;
      if (Object.prototype.hasOwnProperty.call(this.env, name)) {
        return this.env[name]!;
      }
      throw new Error(`unknown name “${name}”`);
    }
    throw new Error("unexpected token");
  }

  /** Full statement / expression value (number or string from `repeat_{…}{…}` or `|` concat). */
  parseValue(): number | string {
    const v = this.parseConcatValue();
    if (this.peek().k !== "eof") {
      throw new Error("unexpected token after expression");
    }
    return v;
  }

  /** `a | b | c` — lower precedence than `+` / `-` (e.g. `1+2|3` → `"33"`). */
  private parseConcatValue(): number | string {
    const parts: (number | string)[] = [];
    parts.push(this.parseAddSubValue());
    while (this.peek().k === "|") {
      this.eat("|");
      parts.push(this.parseAddSubValue());
    }
    if (parts.length === 1) return parts[0]!;
    let acc = "";
    for (const p of parts) {
      acc += typeof p === "number" ? formatMaxCalcNum(p) : p;
    }
    return acc;
  }

  private parseAddSubValue(): number | string {
    let v = this.parseMulDivValue();
    for (;;) {
      const t = this.peek();
      if (t.k === "+" || t.k === "-") {
        if (typeof v !== "number") throw new Error("invalid use of string in + / -");
        this.eat();
        const r = this.parseMulDivValue();
        if (typeof r !== "number") throw new Error("invalid use of string in + / -");
        v = t.k === "+" ? v + r : v - r;
      } else break;
    }
    return v;
  }

  private parseMulDivValue(): number | string {
    let v = this.parsePowValue();
    for (;;) {
      const t = this.peek();
      if (t.k === "*" || t.k === "/") {
        if (typeof v !== "number") throw new Error("invalid use of string in * /");
        this.eat();
        const r = this.parsePowValue();
        if (typeof r !== "number") throw new Error("invalid use of string in * /");
        v = t.k === "*" ? v * r : v / r;
      } else break;
    }
    return v;
  }

  private parsePowValue(): number | string {
    let v = this.parseUnaryValue();
    if (this.peek().k === "^") {
      if (typeof v !== "number") throw new Error("cannot apply ^ to string");
      this.eat();
      const r = this.parsePowValue();
      if (typeof r !== "number") throw new Error("invalid exponent");
      v = Math.pow(v, r);
    }
    return v;
  }

  private parseUnaryValue(): number | string {
    const t = this.peek();
    if (t.k === "+") {
      this.eat();
      return this.parseUnaryValue();
    }
    if (t.k === "-") {
      this.eat();
      const u = this.parseUnaryValue();
      if (typeof u !== "number") throw new Error("invalid unary - on string");
      return -u;
    }
    return this.parsePrimaryValue();
  }

  private parsePrimaryValue(): number | string {
    const t = this.peek();
    if (t.k === "str") {
      this.eat();
      return interpolateEnvInMaxString(t.v, this.env);
    }
    if (t.k === "(") {
      this.eat("(");
      const v = this.parseConcatValue();
      this.eat(")");
      return v;
    }
    if (t.k === "id" && t.name === "repeat") {
      this.eat();
      return this.parseRepeatRestString();
    }
    if (t.k === "id" && t.name === "show") {
      this.eat();
      return this.parseShowRestString();
    }
    return this.parsePrimaryNumber();
  }

  /** `show{L}{R}` — formatted `L | R` for two finite numeric values (panel label | statistic). */
  private parseShowRestString(): string {
    this.eat("{");
    const leftR = sliceUntil(this.toks, this.i, "}");
    if ("err" in leftR) throw new Error(leftR.err);
    this.i = leftR.until + 1;
    const leftToks = [...leftR.slice, { k: "eof" } as const];
    const leftVal = new Parser(leftToks, this.env, this.funcs).parse();
    if (!Number.isFinite(leftVal)) throw new Error("show: left side must be a finite number");

    this.eat("{");
    const rightR = sliceUntil(this.toks, this.i, "}");
    if ("err" in rightR) throw new Error(rightR.err);
    this.i = rightR.until + 1;
    const rightToks = [...rightR.slice, { k: "eof" } as const];
    const rightVal = new Parser(rightToks, this.env, this.funcs).parseValue();
    if (typeof rightVal !== "number" || !Number.isFinite(rightVal)) {
      throw new Error("show: right side must be a finite number");
    }
    return `${formatMaxCalcNum(leftVal)} | ${formatMaxCalcNum(rightVal)}`;
  }

  /** `repeat_{count}{payload}` — count is a numeric expression; payload is `"…"`, number, variable, or expression. */
  private parseRepeatRestString(): string {
    this.eat("_");
    this.eat("{");
    const countR = sliceUntil(this.toks, this.i, "}");
    if ("err" in countR) throw new Error(countR.err);
    this.i = countR.until + 1;
    const countToks = [...countR.slice, { k: "eof" } as const];
    const countVal = new Parser(countToks, this.env, this.funcs).parse();
    if (!Number.isFinite(countVal)) throw new Error("repeat: count must be a finite number");
    const n0 = Math.floor(Math.abs(countVal));
    const n = Math.min(n0, MAX_REPEAT_COUNT);

    this.eat("{");
    const bodyR = sliceUntil(this.toks, this.i, "}");
    if ("err" in bodyR) throw new Error(bodyR.err);
    this.i = bodyR.until + 1;
    const payload = resolveRepeatPayload(bodyR.slice, this.env, this.funcs);
    return payload.repeat(n);
  }

  private parseFuncall(name: string): number {
    const fn1 = UNARY_FUN[name];
    const fn2 = BINARY_FUN[name];
    const userFn = this.funcs && this.funcs[name];
    this.eat("(");
    if (userFn) {
      const args: number[] = [];
      for (let i = 0; i < userFn.params.length; i++) {
        if (i > 0) this.eat(",");
        args.push(this.parseAddSub());
      }
      this.eat(")");
      const childEnv: Record<string, number> = { ...this.env };
      for (let i = 0; i < userFn.params.length; i++) {
        childEnv[userFn.params[i]!] = args[i]!;
      }
      const childToks = lexMaxExpr(userFn.bodySrc);
      if ("err" in childToks) throw new Error(childToks.err);
      const child = new Parser([...childToks, { k: "eof" } as const], childEnv, this.funcs);
      return child.parse();
    }
    if (fn1) {
      const x = this.parseAddSub();
      if (this.peek().k === ",") throw new Error(`${name} expects one argument`);
      this.eat(")");
      return fn1(x);
    }
    if (fn2) {
      const a = this.parseAddSub();
      this.eat(",");
      const b = this.parseAddSub();
      this.eat(")");
      return fn2(a, b);
    }
    throw new Error(`unknown function “${name}”`);
  }

  private parseAggregateRest(macro: "sum" | "prod" | "integral"): number {
    // `sum_{i=a..b}{…}` (TeX-like) or `sum{i=a..b}{…}` — underscore before `{` is optional.
    if (this.peek().k === "_") this.eat("_");
    this.eat("{");
    const iv = this.peek();
    if (iv.k !== "id") throw new Error("expected index variable");
    const indexVar = iv.name;
    this.eat();
    this.eat("=");
    const loR = sliceUntil(this.toks, this.i, "..");
    if ("err" in loR) throw new Error(loR.err);
    this.i = loR.until + 1;
    const hiR = sliceUntil(this.toks, this.i, "}");
    if ("err" in hiR) throw new Error(hiR.err);
    this.i = hiR.until + 1;

    const loToks = [...loR.slice, { k: "eof" } as const];
    const hiToks = [...hiR.slice, { k: "eof" } as const];
    const lo = new Parser(loToks, { ...this.env }, this.funcs).parse();
    const hi = new Parser(hiToks, { ...this.env }, this.funcs).parse();

    this.eat("{");
    const bodyR = sliceUntil(this.toks, this.i, "}");
    if ("err" in bodyR) throw new Error(bodyR.err);
    this.i = bodyR.until + 1;
    const bodyToks = [...bodyR.slice, { k: "eof" } as const];

    if (macro === "sum") {
      const a = Math.ceil(lo);
      const b = Math.floor(hi);
      if (a > b) return 0;
      if (b - a > 1_000_000) throw new Error("sum: range too large (max 1e6 terms)");
      let s = 0;
      for (let k = a; k <= b; k++) {
        const env = { ...this.env, [indexVar]: k };
        s += new Parser(bodyToks, env, this.funcs).parseExpr();
      }
      return s;
    }
    if (macro === "prod") {
      const a = Math.ceil(lo);
      const b = Math.floor(hi);
      if (a > b) return 1;
      if (b - a > 1_000_000) throw new Error("prod: range too large (max 1e6 terms)");
      let p = 1;
      for (let k = a; k <= b; k++) {
        const env = { ...this.env, [indexVar]: k };
        p *= new Parser(bodyToks, env, this.funcs).parseExpr();
      }
      return p;
    }
    return integrateSimpson((x) => {
      const env = { ...this.env, [indexVar]: x };
      return new Parser(bodyToks, env, this.funcs).parseExpr();
    }, lo, hi);
  }
}

/** `s[openIdx]` must be `{`. Returns index of matching `}` or null. Respects `"` strings. */
function findClosingBrace(s: string, openIdx: number): number | null {
  let depth = 1;
  let inStr = false;
  let strEsc = false;
  for (let j = openIdx + 1; j < s.length; j++) {
    const c = s[j]!;
    if (inStr) {
      if (strEsc) strEsc = false;
      else if (c === "\\") strEsc = true;
      else if (c === '"') inStr = false;
      continue;
    }
    if (c === '"') {
      inStr = true;
      continue;
    }
    if (c === "{") depth++;
    else if (c === "}") {
      depth--;
      if (depth === 0) return j;
    }
  }
  return null;
}

/** `for{i=lo..hi}{ body }` — same range convention as `sum_{i=lo..hi}{…}`; value is concat of each run’s last value. */
function tryExtractForLoop(
  part: string,
): { indexVar: string; loSrc: string; hiSrc: string; bodySrc: string } | null {
  const s = part.trim();
  if (!s.startsWith("for{")) return null;
  const hOpen = 3;
  const hClose = findClosingBrace(s, hOpen);
  if (hClose === null) return null;
  const header = s.slice(hOpen + 1, hClose);
  let p = hClose + 1;
  if (p >= s.length || s[p] !== "{") return null;
  const bClose = findClosingBrace(s, p);
  if (bClose === null) return null;
  if (s.slice(bClose + 1).trim() !== "") return null;
  const bodySrc = s.slice(p + 1, bClose);
  const eq = header.indexOf("=");
  const dotdot = header.indexOf("..");
  if (eq < 1 || dotdot < eq + 2) return null;
  const indexVar = header.slice(0, eq).trim();
  if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(indexVar)) return null;
  const loSrc = header.slice(eq + 1, dotdot).trim();
  const hiSrc = header.slice(dotdot + 2).trim();
  if (!loSrc || !hiSrc) return null;
  return { indexVar, loSrc, hiSrc, bodySrc };
}

function runForLoop(
  fp: { indexVar: string; loSrc: string; hiSrc: string; bodySrc: string },
  env: Record<string, number>,
  tables: MaxCalcTables,
  funcs: MaxCalcUserFuncs,
): number | string {
  const loToks = lexMaxExpr(fp.loSrc);
  const hiToks = lexMaxExpr(fp.hiSrc);
  if ("err" in loToks) throw new Error(loToks.err);
  if ("err" in hiToks) throw new Error(hiToks.err);
  const lo = new Parser([...loToks, { k: "eof" } as const], env, funcs).parse();
  const hi = new Parser([...hiToks, { k: "eof" } as const], env, funcs).parse();
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) {
    throw new Error("for: bounds must be finite numbers");
  }
  const a = Math.ceil(lo);
  const b = Math.floor(hi);
  if (a > b) return 0;
  if (b - a > 1_000_000) throw new Error("for: range too large (max 1e6 iterations)");
  let acc = "";
  for (let k = a; k <= b; k++) {
    const loopEnv = { ...env, [fp.indexVar]: k };
    const iter = evaluateStatementSequence(fp.bodySrc, loopEnv, tables, funcs);
    Object.assign(env, loopEnv);
    acc += typeof iter === "number" ? formatMaxCalcNum(iter) : iter;
  }
  return acc;
}

function isTableConstructorRhs(rhs: string): boolean {
  const toks = lexMaxExpr(rhs.trim());
  if ("err" in toks || toks.length < 3) return false;
  const a = toks[0]!;
  const b = toks[1]!;
  return a.k === "id" && a.name === "table" && b.k === "{";
}

function createTableFromRhs(
  tableName: string,
  rhs: string,
  env: Record<string, number>,
  tables: MaxCalcTables,
  funcs: MaxCalcUserFuncs,
): void {
  const toks = lexMaxExpr(rhs.trim());
  if ("err" in toks) throw new Error(toks.err);
  if (toks.length < 4) throw new Error("table: expected table{rows,cols}");
  const t0 = toks[0]!;
  const t1 = toks[1]!;
  if (t0.k !== "id" || t0.name !== "table" || t1.k !== "{") throw new Error("table: expected table{rows,cols}");
  const dim1 = sliceUntilComma(toks, 2);
  if ("err" in dim1) throw new Error(dim1.err);
  const dim2 = sliceUntil(toks, dim1.next, "}");
  if ("err" in dim2) throw new Error(dim2.err);
  const rowsVal = new Parser([...dim1.slice, { k: "eof" } as const], env, funcs).parse();
  const colsVal = new Parser([...dim2.slice, { k: "eof" } as const], env, funcs).parse();
  if (!Number.isFinite(rowsVal) || !Number.isFinite(colsVal)) {
    throw new Error("table: rows and cols must be finite numbers");
  }
  const rows = Math.floor(Math.abs(rowsVal));
  const cols = Math.floor(Math.abs(colsVal));
  if (rows < 1 || cols < 1) throw new Error("table: rows and cols must be at least 1");
  if (rows > MAX_TABLE_SIDE || cols > MAX_TABLE_SIDE) {
    throw new Error(`table: side length exceeds ${MAX_TABLE_SIDE}`);
  }
  if (rows * cols > MAX_TABLE_CELLS) {
    throw new Error(`table: at most ${MAX_TABLE_CELLS} cells`);
  }
  delete env[tableName];
  tables[tableName] = Array.from({ length: rows }, () => Array.from({ length: cols }, () => ""));
}

/**
 * If tokens match `name[expr][expr]=rhs`, assign cell and return payload string; else `null`.
 */
function tryParseTableCellAssignment(
  toks: Tok[],
  env: Record<string, number>,
  tables: MaxCalcTables,
  funcs: MaxCalcUserFuncs,
): string | null {
  if (toks.length < 2) return null;
  const last = toks[toks.length - 1]!;
  if (last.k !== "eof") return null;
  const body = toks.slice(0, -1);
  if (body[0]!.k !== "id") return null;
  const tableName = body[0]!.k === "id" ? body[0]!.name : "";
  if (body[1]?.k !== "[") return null;
  const rowInner = sliceBracketExpr(body, 2);
  if ("err" in rowInner) return null;
  let i = rowInner.end; // first token after row `]`
  if (body[i]?.k !== "[") return null;
  i++;
  const colInner = sliceBracketExpr(body, i);
  if ("err" in colInner) return null;
  i = colInner.end; // first token after col `]`
  if (body[i]?.k !== "=") return null;
  i++;
  const rhsSlice = body.slice(i);
  if (rhsSlice.length === 0) throw new Error("table cell: empty RHS");

  const rowVal = new Parser([...rowInner.slice, { k: "eof" } as const], env, funcs).parse();
  const colVal = new Parser([...colInner.slice, { k: "eof" } as const], env, funcs).parse();
  if (!Number.isFinite(rowVal) || !Number.isFinite(colVal)) {
    throw new Error("table cell: index must be finite");
  }
  const ri = Math.floor(rowVal);
  const ci = Math.floor(colVal);
  const grid = tables[tableName];
  if (!grid) throw new Error(`unknown table “${tableName}”`);
  if (ri < 0 || ci < 0 || ri >= grid.length || ci >= grid[0]!.length) {
    throw new Error(`table cell: index [${ri}][${ci}] out of bounds`);
  }
  const payload = resolveRepeatPayload(rhsSlice, env, funcs);
  grid[ri]![ci] = payload;
  return payload;
}

/**
 * `if{cond}{then}{else}` or `if cond{then}{else}` (optional `else` block).
 * Second form matches e.g. `if i/2=round(i/2){a=1}{b=2}`.
 */
function tryExtractIfElse(
  part: string,
): { condSrc: string; thenSrc: string; elseSrc: string } | null {
  const s = part.trim();
  if (!s.startsWith("if")) return null;
  const rest = s.slice(2).trimStart();
  if (rest.length === 0) return null;

  let condSrc: string;
  let p: number;

  if (rest.startsWith("{")) {
    const hOpen = 0;
    const hClose = findClosingBrace(rest, hOpen);
    if (hClose === null) return null;
    condSrc = rest.slice(hOpen + 1, hClose);
    p = hClose + 1;
  } else {
    let j = 0;
    let depth = 0;
    let inStr = false;
    let strEsc = false;
    while (j < rest.length) {
      const c = rest[j]!;
      if (inStr) {
        if (strEsc) {
          strEsc = false;
        } else if (c === "\\") {
          strEsc = true;
        } else if (c === '"') {
          inStr = false;
        }
        j++;
        continue;
      }
      if (c === '"') {
        inStr = true;
        j++;
        continue;
      }
      if (c === "(" || c === "[") depth++;
      else if (c === ")" || c === "]") depth--;
      if (depth === 0 && c === "{") break;
      j++;
    }
    if (j >= rest.length || rest[j] !== "{") return null;
    condSrc = rest.slice(0, j).trim();
    if (!condSrc) return null;
    p = j;
  }

  if (p >= rest.length || rest[p] !== "{") return null;
  const tClose = findClosingBrace(rest, p);
  if (tClose === null) return null;
  const thenSrc = rest.slice(p + 1, tClose);
  p = tClose + 1;
  let elseSrc = "";
  if (p < rest.length && rest[p] === "{") {
    const eClose = findClosingBrace(rest, p);
    if (eClose === null) return null;
    elseSrc = rest.slice(p + 1, eClose);
    p = eClose + 1;
  }
  if (rest.slice(p).trim() !== "") return null;
  return { condSrc, thenSrc, elseSrc };
}

/** Evaluate a condition to boolean. Supports `=` as equality comparison. */
function evalCondition(
  condSrc: string,
  env: Record<string, number>,
  tables: MaxCalcTables,
  funcs: MaxCalcUserFuncs,
): boolean {
  const s = condSrc.trim();
  let depth = 0;
  let inStr = false;
  let strEsc = false;
  for (let j = 0; j < s.length - 1; j++) {
    const c = s[j]!;
    if (inStr) {
      if (strEsc) { strEsc = false; continue; }
      if (c === "\\") { strEsc = true; continue; }
      if (c === '"') { inStr = false; continue; }
      continue;
    }
    if (c === '"') { inStr = true; continue; }
    if (c === "(" || c === "{" || c === "[") { depth++; continue; }
    if (c === ")" || c === "}" || c === "]") { depth--; continue; }
    if (depth !== 0) continue;
    if (c === "=") {
      const prev = j > 0 ? s[j - 1] : "";
      const next = s[j + 1]!;
      if (prev !== "=" && prev !== "<" && prev !== ">" && next !== "=") {
        const leftSrc = s.slice(0, j).trim();
        const rightSrc = s.slice(j + 1).trim();
        if (leftSrc && rightSrc) {
          const lToks = lexMaxExpr(leftSrc);
          const rToks = lexMaxExpr(rightSrc);
          if (!("err" in lToks) && !("err" in rToks)) {
            const l = new Parser([...lToks, { k: "eof" }], env, funcs).parse();
            const r = new Parser([...rToks, { k: "eof" }], env, funcs).parse();
            return l === r;
          }
        }
      }
    }
  }
  const toks = lexMaxExpr(s);
  if ("err" in toks) throw new Error(toks.err);
  const v = new Parser([...toks, { k: "eof" }], env, funcs).parse();
  return v !== 0;
}

function tryExtractFuncDef(src: string): { name: string; params: string[]; bodySrc: string } | null {
  const m = /^([a-zA-Z_][a-zA-Z0-9_]*)\s*\(([^)]*)\)\s*=(.+)$/.exec(src.trim());
  if (!m) return null;
  const name = m[1]!;
  const paramStr = m[2]!.trim();
  const bodySrc = m[3]!.trim();
  if (!bodySrc) return null;
  const params = paramStr ? paramStr.split(",").map((s) => s.trim()).filter(Boolean) : [];
  for (const p of params) {
    if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(p)) return null;
  }
  return { name, params, bodySrc };
}

/**
 * Evaluate one or more statements separated by `;`.
 * `name = expr` assigns into `env` (mutated). The block’s value is the last statement’s value.
 * Uses the same `env` object for nested parsers so assignments are visible to later statements.
 */
function evaluateStatementSequence(
  src: string,
  env: Record<string, number>,
  tables: MaxCalcTables,
  funcs: MaxCalcUserFuncs,
): number | string {
  const parts = splitBySemicolon(src);
  let last: number | string = 0;
  let any = false;
  for (const part of parts) {
    const p = maybeRewriteDisplayPipeStmt(part.trim());
    if (!p) continue;
    any = true;
    const funcDef = tryExtractFuncDef(p);
    if (funcDef) {
      funcs[funcDef.name] = { params: funcDef.params, bodySrc: funcDef.bodySrc };
      last = "";
      continue;
    }
    const ifEx = tryExtractIfElse(p);
    if (ifEx) {
      const cond = evalCondition(ifEx.condSrc, env, tables, funcs);
      const branch = cond ? ifEx.thenSrc : ifEx.elseSrc;
      if (!branch) { last = ""; continue; }
      last = evaluateStatementSequence(branch, env, tables, funcs);
      continue;
    }
    const forLp = tryExtractForLoop(p);
    if (forLp) {
      last = runForLoop(forLp, env, tables, funcs);
      continue;
    }
    const toks = lexMaxExpr(p);
    if ("err" in toks) throw new Error(toks.err);
    const cell = tryParseTableCellAssignment(toks, env, tables, funcs);
    if (cell !== null) {
      last = cell;
      continue;
    }
    const m = p.match(/^([a-zA-Z_][a-zA-Z0-9_]*)\s*=(.+)$/);
    if (m) {
      const name = m[1]!;
      const rhs = m[2]!.trim();
      if (!rhs) throw new Error(`empty RHS for “${name}”`);
      if (RESERVED_ASSIGN.has(name)) throw new Error(`cannot assign to “${name}”`);
      if (isTableConstructorRhs(rhs)) {
        createTableFromRhs(name, rhs, env, tables, funcs);
        last = "";
        continue;
      }
      const rhsToks = lexMaxExpr(rhs);
      if ("err" in rhsToks) throw new Error(rhsToks.err);
      const val = new Parser(rhsToks, env, funcs).parseValue();
      if (typeof val !== "number") {
        throw new Error(`assignment RHS must be numeric (got string from “${name} = …”)`);
      }
      delete tables[name];
      env[name] = val;
      last = val;
    } else {
      last = new Parser(toks, env, funcs).parseValue();
    }
  }
  if (!any) return 0;
  return last;
}

/**
 * @param vars Optional map of user variables; **mutated** when assignments run. The same object
 * should be passed across `@@…@@` blocks in document order so later blocks see earlier assignments.
 * @param tables Optional string grids for `name=table{r,c}` / `name[i][j]=…`; **mutated** like `vars`.
 */
export function evaluateMaxNumericExpression(
  rawInput: string,
  vars?: Record<string, number>,
  tables?: MaxCalcTables,
  funcs?: MaxCalcUserFuncs,
): MaxCalcResult {
  const raw = rawInput.trim();
  if (!raw) return { ok: true, value: "" };
  const env = vars ?? {};
  const tbl = tables ?? {};
  const fns = funcs ?? {};

  const legacy = /^[\d+\-*/().\s]+$/;
  if (legacy.test(raw) && !raw.includes(";")) {
    try {
      const v = new Function(`"use strict"; return (${raw})`)() as unknown;
      if (typeof v !== "number" || Number.isNaN(v)) {
        return { ok: false, error: "Not a number" };
      }
      return { ok: true, value: formatMaxCalcNum(v) };
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      return { ok: false, error: msg };
    }
  }

  try {
    const v = evaluateStatementSequence(raw, env, tbl, fns);
    if (typeof v === "string") {
      return { ok: true, value: v };
    }
    if (!Number.isFinite(v)) {
      return { ok: false, error: "result is not finite" };
    }
    return { ok: true, value: formatMaxCalcNum(v) };
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    return { ok: false, error: msg };
  }
}
